import { UserRole } from '@enums/user.enum';
import { CancellationAdjustmentType, CancelledReason } from '@enums/transport.enum';
import { AdjustmentType } from '@enums/additional.enum';
import { Helpers } from '@utils/helpers';

/**
 * DTO para los umbrales de ajuste de cancelación
 * Basado en la respuesta de /transports/cancel-transport/adjustments_thresholds/
 */
export class AdjustmentRangeOptionsDto {
  driver_price_thresholds: Record<string, string> = {};
  client_percentage_thresholds: Record<string, string> = {};

  constructor(data?: any) {
    if (data) {
      this.copyFrom(data);
    }
  }

  /**
   * Crea una instancia de AdjustmentRangeOptionsDto a partir de un objeto JSON especificado.
   * @param json El objeto JSON a partir del cual crear la instancia.
   * @returns Una nueva instancia con los datos del JSON.
   */
  static fromJson(json: any): AdjustmentRangeOptionsDto {
    return new AdjustmentRangeOptionsDto(json);
  }

  /**
   * Convierte la instancia actual en un objeto JSON plano.
   * @returns Un objeto con las propiedades de la clase.
   */
  toJson(): any {
    return {
      driver_price_thresholds: { ...this.driver_price_thresholds },
      client_percentage_thresholds: { ...this.client_percentage_thresholds }
    };
  }

  /**
   * Actualiza las propiedades de la instancia actual con los valores del objeto proporcionado.
   * @param data Objeto con los nuevos valores.
   */
  copyFrom(data: any): void {
    if (!data) return;
    if (data.driver_price_thresholds !== undefined) {
      this.driver_price_thresholds = { ...data.driver_price_thresholds };
    }
    if (data.client_percentage_thresholds !== undefined) {
      this.client_percentage_thresholds = { ...data.client_percentage_thresholds };
    }
  }
}

/**
 * DTO para la solicitud de cancelación de transporte
 */
export class CancelTransportDto {
  id?: number;
  cancelled_by: UserRole | string = '';
  cancelled_reason: CancelledReason | string = '';
  message: string = '';
  driver_adjustment_type: CancellationAdjustmentType | null = null;
  pay_price_driver: number = 0;
  client_adjustment_type: AdjustmentType = AdjustmentType.PENALTY;
  pay_percentage_client: number = 0;
  hours_left?: number;
  driver_suggested_price?: number;
  client_manual_entry: boolean = false;
  driver_manual_entry: boolean = false;

  constructor(data?: any) {
    if (data) {
      this.copyFrom(data);
    }
  }

  /**
   * Crea una instancia de CancelTransportDto a partir de un objeto JSON especificado.
   * @param json El objeto JSON a partir del cual crear la instancia.
   * @returns Una nueva instancia con los datos del JSON.
   */
  static fromJson(json: any): CancelTransportDto {
    return new CancelTransportDto(json);
  }

  /**
   * Convierte la instancia actual en un objeto JSON plano.
   * @returns Un objeto con las propiedades de la clase.
   */
  toJson(): any {
    return {
      cancelled_by: this.cancelled_by,
      cancelled_reason: this.cancelled_reason,
      message: this.message,
      driver_adjustment_type: this.driver_adjustment_type,
      pay_price_driver: this.pay_price_driver,
      client_adjustment_type: this.client_adjustment_type,
      pay_percentage_client: this.pay_percentage_client,
      client_manual_entry: this.client_manual_entry,
      driver_manual_entry: this.driver_manual_entry
    };
  }

  /**
   * Actualiza las propiedades de la instancia actual con los valores del objeto proporcionado.
   * @param data Objeto con los nuevos valores.
   */
  copyFrom(data: any): void {
    if (!data) return;
    this.cancelled_by = data.cancelled_by ?? this.cancelled_by;
    this.cancelled_reason = data.cancelled_reason ?? this.cancelled_reason;
    this.message = data.message ?? this.message;
    this.driver_adjustment_type = data.driver_adjustment_type ?? this.driver_adjustment_type;
    this.pay_price_driver = data.pay_price_driver ?? this.pay_price_driver;
    this.client_adjustment_type = data.client_adjustment_type ?? this.client_adjustment_type;
    this.pay_percentage_client = data.pay_percentage_client ?? this.pay_percentage_client;
    this.hours_left = data.hours_left ?? this.hours_left;
    this.driver_suggested_price = data.driver_suggested_price ?? this.driver_suggested_price;
    this.client_manual_entry = !Helpers.isNullOrUndefined(data.client_manual_entry) ? data.client_manual_entry : false;
    this.driver_manual_entry = !Helpers.isNullOrUndefined(data.driver_manual_entry) ? data.driver_manual_entry : false;
  }

  /**
   * Genera un objeto plano adaptado a los requisitos exactos del backend.
   * Realiza el mapeo de enums específicos de cancelación (CLIENT, BONUS, NONE).
   * @returns Un objeto plano listo para ser enviado por POST/PUT.
   */
  toSave(): any {
    const data: any = {
      cancelled_reason: this.cancelled_reason,
      message: this.message
    };

    // Mapeo de cancelled_by: CLIENT_USER -> CLIENT
    if (this.cancelled_by === UserRole.CLIENT_USER) {
      data.cancelled_by = 'CLIENT';
    } else {
      data.cancelled_by = this.cancelled_by;
    }

    // Mapeo de driver_adjustment_type: PAY -> BONUS, NO_PAY -> null
    if (this.driver_adjustment_type === CancellationAdjustmentType.PAY) {
      data.driver_adjustment_type = AdjustmentType.BONUS;
    } else if (this.driver_adjustment_type === CancellationAdjustmentType.NO_PAY) {
      data.driver_adjustment_type = null;
      data.pay_price_driver = 0;
    } else {
      data.driver_adjustment_type = this.driver_adjustment_type;
    }

    data.pay_price_driver = this.pay_price_driver;

    // Mapeo de client_adjustment_type: NO_PAY -> null
    if ((this.client_adjustment_type as any) === 'NO_PAY') {
      data.client_adjustment_type = null;
    } else {
      data.client_adjustment_type = this.client_adjustment_type;
    }

    data.pay_percentage_client = this.pay_percentage_client;
    data.client_manual_entry = this.client_manual_entry;
    data.driver_manual_entry = this.driver_manual_entry;

    return data;
  }
}


export class CancelTransportInfo {
  cancelled_by: 'ADMIN' | 'CLIENT' | 'DRIVER' | null = null;
  cancelled_reason: string | null = null;
  message: string = '';
  driver_adjustment_type: AdjustmentType | null = null;
  pay_price_driver: number | null = null;
  client_adjustment_type: AdjustmentType | null = null;
  pay_percentage_client: number | null = null;
  hours_left: number = 0;
  driver_suggested_price: number = 0;
  client_suggested_percentage: number = 0;
  client_manual_entry: boolean = false;
  driver_manual_entry: boolean = false;

  constructor(data?: any) {
    if (data) {
      this.copyFrom(data);
    }
  }

  static fromJson(json: any): CancelTransportInfo {
    return new CancelTransportInfo(json);
  }

  copyFrom(data: any): void {
    if (!data) return;
    this.cancelled_by = data.cancelled_by ?? this.cancelled_by;
    this.cancelled_reason = data.cancelled_reason ?? this.cancelled_reason;
    this.message = data.message ?? this.message;
    this.driver_adjustment_type = data.driver_adjustment_type ?? this.driver_adjustment_type;
    this.pay_price_driver = data.pay_price_driver ?? this.pay_price_driver;
    this.client_adjustment_type = data.client_adjustment_type ?? this.client_adjustment_type;
    this.pay_percentage_client = data.pay_percentage_client ?? this.pay_percentage_client;
    this.hours_left = data.hours_left ?? this.hours_left;
    this.driver_suggested_price = data.driver_suggested_price ?? this.driver_suggested_price;
    this.client_suggested_percentage = data.client_suggested_percentage ?? this.client_suggested_percentage;
    this.client_manual_entry = !Helpers.isNullOrUndefined(data.client_manual_entry) ? data.client_manual_entry : false;
    this.driver_manual_entry = !Helpers.isNullOrUndefined(data.driver_manual_entry) ? data.driver_manual_entry : false;
  }

  /**
   * Convierte esta informacin en un CancelTransportDto
   * lgica de mapeo:
   * - driver_adjustment_type null -> NO_PAY
   * - driver_adjustment_type BONUS -> PAY
   */
  toCancelTransport(): CancelTransportDto {
    const dto = new CancelTransportDto();

    dto.cancelled_by = this.cancelled_by || UserRole.ADMIN; // Default fallback
    dto.cancelled_reason = this.cancelled_reason || '';
    dto.message = this.message || '';

    // Mapeo de driver_adjustment_type
    if (this.driver_adjustment_type === null) {
      dto.driver_adjustment_type = CancellationAdjustmentType.NO_PAY;
    } else if (this.driver_adjustment_type === AdjustmentType.BONUS) {
      dto.driver_adjustment_type = CancellationAdjustmentType.PAY;
    } else if (this.driver_adjustment_type === AdjustmentType.PENALTY) {
      dto.driver_adjustment_type = CancellationAdjustmentType.PENALTY;
    } else {
      dto.driver_adjustment_type = CancellationAdjustmentType.NO_PAY;
    }

    dto.pay_price_driver = this.pay_price_driver || 0;

    // client_adjustment_type se mantiene igual si existe, sino PENALTY por defecto
    dto.client_adjustment_type = this.client_adjustment_type || AdjustmentType.PENALTY;

    // Nuevos campos opcionales
    dto.hours_left = this.hours_left;
    dto.driver_suggested_price = this.driver_suggested_price;
    dto.pay_percentage_client = this.client_suggested_percentage;
    dto.client_manual_entry = this.client_manual_entry;
    dto.driver_manual_entry = this.driver_manual_entry;

    return dto;
  }
}

